import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { RevealLines } from "@/components/common/Reveal";
import { GoldButton, GhostButton } from "@/components/common/Buttons";
import { IMAGES } from "@/lib/content";

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroMain} alt="Atelier de fabrication — découpe laser" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-[#07080B]/70 to-[#07080B]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07080B]/90 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-[1400px] w-full px-5 sm:px-8 pb-20 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="eyebrow mb-7 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[var(--gold)]/70" />
          Holding d'acquisition · France 🇫🇷 Suisse 🇨🇭
        </motion.div>

        <h1 className="font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl text-white max-w-5xl">
          <RevealLines
            lines={["Construire ensemble", "le réseau de référence"]}
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-gold"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.43, ease: [0.22, 1, 0.36, 1] }}
            >
              de nos métiers.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 font-light leading-relaxed"
        >
          Losta Group est une holding spécialisée dans l'acquisition et le développement d'entreprises disposant d'un
          savoir-faire reconnu dans la communication visuelle et les activités techniques complémentaires.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <GoldButton to="/ceder-votre-entreprise" testid="hero-cta-ceder">
            Céder mon entreprise <ArrowUpRight className="h-4 w-4" />
          </GoldButton>
          <GhostButton to="/groupe" testid="hero-cta-decouvrir">
            Découvrir Losta Group
          </GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-12 flex items-center gap-6 text-xs text-slate-400 font-mono tracking-wider"
        >
          <span className="hidden sm:flex items-center gap-2"><ArrowDown className="h-3.5 w-3.5 text-gold animate-bounce" /> Faites défiler</span>
          <span className="h-px flex-1 max-w-[120px] bg-white/10" />
          <span>Acquisition · Transmission · Développement</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
